import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import React, { useContext, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { AiOutlineAppstoreAdd, AiOutlineImport } from "react-icons/ai";
import { BsFillTelephoneFill } from "react-icons/bs";
import { FaWhatsapp } from "react-icons/fa";
import { IoCallSharp } from "react-icons/io5";
import { PiMicrosoftExcelLogo } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDateWithAmPm } from "../../utils/DateFormat/dateFormatWithTimeAmPm";
import { fetchController } from "../../utils/FetchController/fetchController";
import Loader from "../../utils/Loader/Loader";
import PaginationSizeSelect from "../../utils/Loader/PaginationSizeSelect";
import CountryFlag from "../../utils/CountryList/CountryFlag";
import { isoFromDialCode, isoFromPhone } from "../../utils/CountryList/phoneFlag";
import Paginator from "../../utils/Loader/Paginator";
import { getHostDomain } from "../../utils/utils";
import AddContact from "../Modals/AddContact";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import Table from "../Tables/Table";
import BulkImportContactModal from "./BulkImportContactModal";
import StartCampaignModal from "./StartCampaignModal";
import { HEADS_DATA } from "./constants";

import { MdDelete, MdOutlineMessage } from "react-icons/md";
import ConfirmationModal from "../Modals/Confirmation";
import InitiateConversationModal from "../Modals/InitiateConversationModal";

const timeAgo = (dateStr) => {
  if (!dateStr) return "—";
  try {
    // API returns UTC timestamps without Z — append Z so Date parses as UTC
    const utcStr = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
    return formatDistanceToNow(new Date(utcStr), { addSuffix: true });
  } catch {
    return "—";
  }
};

const ContactTable = () => {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [status, setStatus] = useState("idle");
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme } = agency;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [paginationSize, setPaginationSize] = useState(50);

  const [tableData, setTableData] = useState([]);
  const [heads, setHeads] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set()); // <- selection set
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const endpoint = `/api/v1/contact/list?size=${paginationSize}&page=${page}`;
  const url = baseUrl;
  const hitFetch = async () => {
    try {
      setStatus("loading");
      const response = await fetchController(url + endpoint, "GET");

      if (!response?.data) {
        setStatus("failure");
        return;
      }

      setPages(response.data.pages);

      const updatedData = response.data.items.map((item) => {
        const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim() || "—";
        const phone = item.channels?.national_number || item.channels?.telephone;
        // Resolve country: prefer stored dial_code (canonical source,
        // tolerates "91" / "+91" / "IN" / 91), fall back to parsing
        // the E.164 number when no dial_code is stored.
        const iso =
          isoFromDialCode(item.channels?.dial_code) ||
          isoFromPhone(item.channels?.telephone);

        return {
          value: item,
          status: false,
          currentStatus: selectedItems.has(item.id),
          name: (
            <Flex align="center" gap={2}>
              <Avatar name={fullName === "—" ? undefined : fullName} size="xs" />
              <Text fontSize="sm">{fullName}</Text>
            </Flex>
          ),
          email: item.email,
          contact: phone ? (
            <Flex align="center" gap={1.5}>
              {iso && <CountryFlag iso={iso} width={18} />}
              <Text fontSize="sm">{phone}</Text>
            </Flex>
          ) : (
            "-"
          ),
          channels: (
            <Flex gap={2} direction="row" wrap="nowrap">
              {phone && (
                <Tooltip label={phone}>
                  <span>
                    <BsFillTelephoneFill
                      onClick={() => { navigator.clipboard.writeText(phone); toast.success("Copied"); }}
                      cursor="pointer"
                      color="#0f52b9"
                    />
                  </span>
                </Tooltip>
              )}
              {item.channels?.whatsapp && (
                <Tooltip label={item.channels.whatsapp}>
                  <img
                    onClick={() => { navigator.clipboard.writeText(item.channels.whatsapp); toast.success("Copied"); }}
                    src="/icons/whatsapp.svg"
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                    alt="whatsapp_icon"
                  />
                </Tooltip>
              )}
              {item.channels?.instagram && (
                <Tooltip label={item.channels.instagram}>
                  <img
                    onClick={() => { navigator.clipboard.writeText(item.channels.instagram); toast.success("Copied"); }}
                    src="/icons/instagram.svg"
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                    alt="instagram_icon"
                  />
                </Tooltip>
              )}
              {item.channels?.slack && (
                <Tooltip label={item.channels.slack}>
                  <img
                    onClick={() => { navigator.clipboard.writeText(item.channels.slack); toast.success("Copied"); }}
                    src="/icons/slack.svg"
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                    alt="slack_icon"
                  />
                </Tooltip>
              )}
              {item.channels?.microsoft_teams && (
                <Tooltip label={item.channels.microsoft_teams}>
                  <img
                    onClick={() => { navigator.clipboard.writeText(item.channels.microsoft_teams); toast.success("Copied"); }}
                    src="/icons/teams.svg"
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                    alt="teams_icon"
                  />
                </Tooltip>
              )}
              {!phone && !item.channels?.whatsapp && !item.channels?.instagram && !item.channels?.slack && !item.channels?.microsoft_teams && (
                <Text fontSize="xs" color="gray.400">—</Text>
              )}
            </Flex>
          ),
          "First added on": item.created_at ? (
            <Tooltip label={formatDateWithAmPm(item.created_at, "short")}>
              <Text as="span" fontSize="sm" cursor="default">{timeAgo(item.created_at)}</Text>
            </Tooltip>
          ) : "—",
          "Last seen": item.last_seen ? (
            <Tooltip label={formatDateWithAmPm(item.last_seen, "short")}>
              <Text as="span" fontSize="sm" cursor="default">{timeAgo(item.last_seen)}</Text>
            </Tooltip>
          ) : "—",
          id: item.id,
        };
      });

      setTableData(updatedData);
      setHeads(HEADS_DATA.map((key) => ({ Header: key, accessor: key })));

      setStatus("success");
    } catch (error) {
      console.error("Error fetching data:", error);
      setStatus("failure");
    }
  };

  useEffect(() => {
    hitFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, paginationSize]);


  const tableRows = tableData.map((row, index) => ({
    ...row,
  }));

  const csvData = tableData
    .filter((row) => selectedItems.has(row.id))
    .map(({ status, channels, currentStatus, ...row }) => row);

  const {
    isOpen: isCampaignModalOpen,
    onOpen: openCampaignModal,
    onClose: closeCampaignModal,
  } = useDisclosure();

  const clearStatusFromTable = () => {
    setTableData((prev) =>
      prev.map((tableRow) => ({
        ...tableRow,
        currentStatus: false,
      }))
    );
    setSelectedItems(new Set());
  };

  const {
    isOpen: isBulkImportModalOpen,
    onClose: closeBulkImportModal,
    onOpen: openBulkImportModal,
  } = useDisclosure();

  const {
    isOpen: isInitiateModalOpen,
    onOpen: openInitiateModal,
    onClose: closeInitiateModal,
  } = useDisclosure();
  const [initiateContact, setInitiateContact] = useState(null);
  const [outboundChannel, setOutboundChannel] = useState(null);

  const handleInitiateConversation = (contact) => {
    setInitiateContact(contact);
    setOutboundChannel(null);
    openInitiateModal();
  };
  const onGotofirstPage = () => setPage(1);

 // inside ContactTable component
const handleConfirmDelete = async () => {
  try {
    const ids = Array.from(selectedItems).map(String); // ensure strings
    await fetchController(
      `${baseUrl}/api/v1/contact/multiple`, // exact path
      "DELETE",
      ids // your fetchController should JSON.stringify this and set Content-Type
    );
    toast.success("Deleted selected contacts!");
    setSelectedItems(new Set());
    await hitFetch();
  } catch (error) {
    console.error("Bulk delete error:", error);
    toast.error("Failed to delete selected contacts");
  } finally {
    setIsConfirmOpen(false);
  }
};


  return (
    <>
      <Flex direction={"column"} gap={3} w={"100%"}>
        <Flex gap={2} w={"100%"} alignItems="center" p={2}>
          <Heading color={textColor} fontSize="xl">
            Contacts
          </Heading>

          <Button
            colorScheme={buttonColorScheme}
            onClick={() => onOpen()}
            size={"sm"}
          >
            {"Add Contact"}
          </Button>
        </Flex>

        <Flex gap={4} marginBlock={3} justifyContent={"space-between"}>
          <Box>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                colorScheme={buttonColorScheme}
                isDisabled={!csvData.length}
                size="sm"
                leftIcon={<MdOutlineMessage />}
              >
                Outbound {csvData.length > 0 ? `(${csvData.length})` : ""}
              </MenuButton>
              <MenuList>
                <MenuItem icon={<IoCallSharp />} onClick={openCampaignModal}>
                  Voice Call
                </MenuItem>
                <MenuItem
                  icon={<FaWhatsapp />}
                  onClick={() => {
                    setOutboundChannel("whatsapp");
                    setInitiateContact(null);
                    openInitiateModal();
                  }}
                >
                  WhatsApp Message
                </MenuItem>
                <MenuItem
                  icon={<BsFillTelephoneFill />}
                  onClick={() => {
                    setOutboundChannel("sms");
                    setInitiateContact(null);
                    openInitiateModal();
                  }}
                >
                  SMS Message
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                colorScheme={buttonColorScheme}
                size={"sm"}
              >
                Bulk
              </MenuButton>
              <MenuList>
                <CSVLink data={csvData} style={{ textDecoration: "none" }}>
                  <MenuItem
                    icon={<PiMicrosoftExcelLogo />}
                    isDisabled={!csvData.length}
                  >
                    Export CSV
                  </MenuItem>
                </CSVLink>
                <MenuItem
                  icon={<AiOutlineImport />}
                  onClick={openBulkImportModal}
                  css={`
                    &:hover {
                      color: #1675e0;
                    }
                  `}
                >
                  Import CSV
                </MenuItem>
              </MenuList>
            </Menu>

            <Button
              colorScheme={buttonColorScheme}
              size={"sm"}
              leftIcon={<AiOutlineAppstoreAdd />}
              onClick={() => navigate("/contacts/custom-field")}
              ml={2}
            >
              Custom Fields
            </Button>

            {/* Bulk delete button */}
            {selectedItems.size > 0 && (
              <Button
                colorScheme="red"
                size={"sm"}
                ml={2}
                onClick={() => setIsConfirmOpen(true)}
              >
                <MdDelete color="white" /> ({selectedItems.size})
              </Button>
            )}
          </Box>
        </Flex>

        {status === "loading" ? (
          <Loader />
        ) : (
          <Table
            fetch={hitFetch}
            columns={heads}
            data={tableRows}
            table="contact"
            enableRowSelection={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            onInitiateConversation={handleInitiateConversation}
          />
        )}
      </Flex>

      <Flex
        justifyContent={"flex-end"}
        alignItems={"center"}
        marginBlock={2}
        gap={2}
        w={"100%"}
      >
        <PaginationSizeSelect
          paginationSize={paginationSize}
          setPaginationSize={setPaginationSize}
          tableLength={tableData.length}
        />
        <Paginator
          colorScheme={buttonColorScheme}
          onChangePage={setPage}
          page={page}
          pages={pages}
        />
      </Flex>

      {isOpen && (
        <AddContact
          onGotofirstPage={onGotofirstPage}
          fetchContact={hitFetch}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
        />
      )}
      {csvData.length > 0 && (
        <StartCampaignModal
          clearStatusFromTable={clearStatusFromTable}
          isOpen={isCampaignModalOpen}
          onClose={closeCampaignModal}
          contacts={csvData}
        />
      )}
      {isBulkImportModalOpen && (
        <BulkImportContactModal
          fetchContacts={hitFetch}
          onGotofirstPage={onGotofirstPage}
          isOpen={isBulkImportModalOpen}
          onClose={closeBulkImportModal}
        />
      )}

      {/* Confirmation modal for bulk delete */}
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        table="contacts"
      />

      {isInitiateModalOpen && (initiateContact || csvData.length > 0) && (
        <InitiateConversationModal
          isOpen={isInitiateModalOpen}
          onClose={closeInitiateModal}
          contact={initiateContact}
          contacts={initiateContact ? null : csvData}
          preselectedChannel={outboundChannel}
        />
      )}
    </>
  );
};

export default ContactTable;
